(() => {
  if (window.FPSampleWidgetBootstrap) {
    window.FPSampleWidgetBootstrap.bind();
    return;
  }

  let config;
  let host;
  let headerLauncher;
  let error;
  let sectionId;

  const widgetSelector = 'fp-sample-widget[data-fp-sample-widget]';
  const dismissedKey = 'fp-sample-widget-dismissed';
  let latestUrl = location.href;
  let stale = true;
  let generation = 0;
  let activeRequest;
  let pendingOpen;
  let pendingLauncher;
  let cssLoading;
  let scriptLoading;

  function bind() {
    const nextConfig = document.querySelector('[data-fp-sample-bootstrap]');
    const nextHost = nextConfig?.closest('.shopify-section');
    if (!nextHost || !nextConfig?.dataset.sectionId) return;
    const nextHeaderLauncher = document.querySelector('[data-sample-widget-header-launcher]');
    if (nextConfig === config && nextHost === host && nextHeaderLauncher === headerLauncher) return;
    const contentRebound = nextConfig !== config || nextHost !== host;

    generation += 1;
    activeRequest?.abort();
    pendingLauncher?.removeAttribute('aria-busy');
    activeRequest = null;
    pendingOpen = null;
    pendingLauncher = null;
    config = nextConfig;
    host = nextHost;
    error = host.querySelector('[data-fp-sample-bootstrap-error]');
    sectionId = config.dataset.sectionId;
    headerLauncher = nextHeaderLauncher;
    if (contentRebound) {
      latestUrl = location.href;
      stale = !host.querySelector(widgetSelector);
    }

    if (headerLauncher) {
      const isLink = config.dataset.headerAction === 'link';
      const tagName = isLink ? 'A' : 'BUTTON';
      if (headerLauncher.tagName !== tagName) {
        const replacement = document.createElement(tagName);
        for (const attribute of headerLauncher.attributes) replacement.setAttribute(attribute.name, attribute.value);
        replacement.append(...headerLauncher.childNodes);
        headerLauncher.replaceWith(replacement);
        headerLauncher = replacement;
      }
      if (isLink) {
        headerLauncher.href = config.dataset.headerUrl;
        headerLauncher.removeAttribute('type');
        headerLauncher.removeAttribute('data-sample-widget-launcher');
        headerLauncher.removeAttribute('aria-haspopup');
      } else {
        headerLauncher.removeAttribute('href');
        headerLauncher.type = 'button';
        headerLauncher.setAttribute('data-sample-widget-launcher', '');
        headerLauncher.setAttribute('aria-haspopup', 'dialog');
      }
      headerLauncher.hidden = config.dataset.showHeaderLauncher !== 'true';
      headerLauncher.setAttribute('aria-label', config.dataset.headerLabel || config.dataset.launcherAriaLabel);
      const icon = host.querySelector('[data-sample-widget-header-icon]');
      if (icon?.content) headerLauncher.querySelector('[data-fp-sample-icon]')?.replaceWith(icon.content.cloneNode(true));
      const tooltip = headerLauncher.querySelector('[data-fp-sample-tooltip]');
      if (tooltip) tooltip.textContent = config.dataset.headerLabel || config.dataset.launcherTooltip;
      const dialog = host.querySelector(`${widgetSelector} [data-sample-widget-dialog]`);
      if (dialog && !isLink) headerLauncher.setAttribute('aria-controls', dialog.id);
      else headerLauncher.removeAttribute('aria-controls');
    }
  }

  try {
    if (sessionStorage.getItem(dismissedKey) === '1') document.documentElement.classList.add(dismissedKey);
  } catch (_) { /* The widget still works without session storage. */ }

  function showError() {
    if (error) error.hidden = false;
  }

  function clearError() {
    if (error) error.hidden = true;
  }

  function requestUrl(href) {
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) throw new Error('Destino de amostras inválido');
    for (const name of ['preview_theme_id', '_fd', 'pb', 'locale', 'country', 'currency']) {
      const current = new URL(location.href).searchParams.get(name);
      if (current && !url.searchParams.has(name)) url.searchParams.set(name, current);
    }
    const theme = window.Shopify?.theme;
    if (theme?.id && theme.role === 'unpublished') url.searchParams.set('preview_theme_id', String(theme.id));
    url.searchParams.set('section_id', sectionId);
    url.hash = '';
    return url;
  }

  async function fetchSection(href, controller) {
    const url = requestUrl(href);
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Amostras: HTTP ${response.status}`);
      const html = await response.text();
      if (html.includes('Liquid error')) throw new Error('Seção de amostras incompleta');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const marker = parsed.querySelector('[data-fp-sample-bootstrap]');
      if (marker?.dataset.sectionId !== sectionId) throw new Error('Seção de amostras incorreta');
      const widget = parsed.querySelector(widgetSelector);
      if (widget && (widget.dataset.sectionId !== sectionId || !widget.querySelector('[data-sample-widget-dialog]'))) {
        throw new Error('Widget de amostras incompleto');
      }
      return widget;
    } finally {
      clearTimeout(timeout);
    }
  }

  function loadCss(href) {
    if (cssLoading) return cssLoading;
    const existing = document.querySelector('link[data-fp-sample-widget-css]');
    if (existing?.dataset.loaded === 'true') return Promise.resolve();
    existing?.remove();
    cssLoading = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.fpSampleWidgetCss = '';
      link.onload = () => { link.dataset.loaded = 'true'; resolve(); };
      link.onerror = () => { link.remove(); reject(new Error('CSS das amostras indisponível')); };
      document.head.append(link);
    }).catch(failure => { cssLoading = null; throw failure; });
    return cssLoading;
  }

  function loadScript(src) {
    if (customElements.get('fp-sample-widget')) return Promise.resolve();
    if (scriptLoading) return scriptLoading;
    scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        if (customElements.get('fp-sample-widget')) resolve();
        else { script.remove(); reject(new Error('Widget de amostras indisponível')); }
      };
      script.onerror = () => { script.remove(); reject(new Error('Widget de amostras indisponível')); };
      document.head.append(script);
    }).catch(failure => { scriptLoading = null; throw failure; });
    return scriptLoading;
  }

  async function loadAssets(widget) {
    const css = widget.dataset.dialogCss || config.dataset.dialogCss;
    const script = widget.dataset.widgetScript || config.dataset.widgetScript;
    if (!css || !script) throw new Error('Arquivos de amostras indisponíveis');
    await Promise.all([loadCss(css), loadScript(script)]);
  }

  async function resolveWidget(turn, controller) {
    const existing = host.querySelector(widgetSelector);
    if (!stale && existing && !existing.hidden) return existing;

    const target = new URL(latestUrl, location.href);
    if (target.origin !== location.origin) throw new Error('Produto de amostras inválido');
    const isProduct = /\/products\/[^/]+/.test(target.pathname);
    let rendered = isProduct ? await fetchSection(target.href, controller) : null;
    if (turn !== generation) return null;
    const generic = !rendered;
    if (generic) rendered = await fetchSection(config.dataset.homeUrl, controller);
    if (turn !== generation) return null;
    if (!rendered) throw new Error('Amostras indisponíveis');

    await loadAssets(rendered);
    if (turn !== generation) return null;

    const widget = document.importNode(rendered, true);
    existing?.remove();
    if (generic && !existing) {
      widget.querySelector('.fp-sample-widget__launcher')?.style.setProperty('display', 'none');
      widget.querySelector('.fp-sample-widget__dismiss')?.style.setProperty('display', 'none');
    }
    host.append(widget);
    stale = false;
    const dialog = widget.querySelector('[data-sample-widget-dialog]');
    if (headerLauncher?.matches('[data-sample-widget-launcher]') && dialog) headerLauncher.setAttribute('aria-controls', dialog.id);
    return widget;
  }

  async function openCurrent(launcher) {
    if (pendingOpen) return;
    const turn = generation;
    const controller = new AbortController();
    activeRequest = controller;
    pendingLauncher = launcher;
    launcher.setAttribute('aria-busy', 'true');
    clearError();
    pendingOpen = (async () => {
      const widget = await resolveWidget(turn, controller);
      if (!widget || turn !== generation) return;
      await loadAssets(widget);
      if (turn !== generation) return;
      if (!launcher.isConnected) {
        const replacement = headerLauncher?.hidden ? widget.querySelector('[data-sample-widget-launcher]') : headerLauncher;
        replacement?.focus({ preventScroll: true });
      }
      widget.open();
    })().catch(failure => {
      if (turn !== generation) return;
      showError();
    }).finally(() => {
      if (turn === generation) {
        launcher.removeAttribute('aria-busy');
        activeRequest = null;
        pendingOpen = null;
        pendingLauncher = null;
      }
    });
    await pendingOpen;
  }

  document.addEventListener('fp:connected-product-swapped', event => {
    latestUrl = event.detail?.url || event.detail?.targetUrl || location.href;
    generation += 1;
    activeRequest?.abort();
    pendingLauncher?.removeAttribute('aria-busy');
    activeRequest = null;
    pendingOpen = null;
    pendingLauncher = null;
    stale = true;
    clearError();
    const widget = host.querySelector(widgetSelector);
    if (widget) {
      widget.setAttribute('data-fp-sample-stale', '');
      widget.querySelector('[data-sample-widget-dialog]')?.setAttribute('inert', '');
    }
    widget?.close?.();
    headerLauncher?.removeAttribute('aria-controls');
  });

  document.addEventListener('shopify:section:unload', event => {
    if (!host || !event.target?.contains(host)) return;
    generation += 1;
    activeRequest?.abort();
    pendingLauncher?.removeAttribute('aria-busy');
    activeRequest = null;
    pendingOpen = null;
    pendingLauncher = null;
    host = null;
    config = null;
    error = null;
    sectionId = null;
    if (headerLauncher) headerLauncher.hidden = true;
  });
  document.addEventListener('shopify:section:load', bind);

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const dismiss = target?.closest('[data-sample-widget-dismiss]');
    if (dismiss && host?.contains(dismiss)) {
      event.preventDefault();
      event.stopPropagation();
      document.documentElement.classList.add(dismissedKey);
      try { sessionStorage.setItem(dismissedKey, '1'); } catch (_) { /* noop */ }
      return;
    }

    const launcher = target?.closest('[data-sample-widget-launcher]');
    const pageLauncher = launcher?.matches('[data-sample-widget-page-launcher]') && launcher.closest('.fp-samples-navigation, [data-fp-samples]');
    if (!launcher || !host || (launcher !== headerLauncher && !host.contains(launcher) && !pageLauncher)) return;
    event.preventDefault();
    event.stopPropagation();
    void openCurrent(launcher);
  }, true);

  window.FPSampleWidgetBootstrap = { bind };
  bind();
})();
